import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie_consent', 'declined');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[9998] p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-[#333] text-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-5 md:p-6">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="hidden sm:flex w-12 h-12 bg-white/10 rounded-xl items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6" />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">We Value Your Privacy</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                        By clicking "Accept All", you consent to our use of cookies. 
                        Read our{' '}
                        <Link to="/cookies" className="underline hover:text-white">
                          Cookies Policy
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="underline hover:text-white">
                          Privacy Policy
                        </Link>{' '}
                        to learn more.
                      </p>
                    </div>
                    
                    {/* Close button for mobile */}
                    <button
                      onClick={handleDecline}
                      className="sm:hidden p-1 hover:bg-white/10 rounded-lg transition-colors"
                      aria-label="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 mt-5">
                    <button
                      onClick={handleAccept}
                      className="px-6 py-2.5 bg-white text-[#333] rounded-full font-medium hover:bg-gray-100 transition-colors text-sm"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={handleDecline}
                      className="px-6 py-2.5 bg-white/10 text-white border border-white/15 rounded-full font-medium hover:bg-white/20 transition-colors text-sm"
                    >
                      Essential Only
                    </button>
                    <Link
                      to="/cookies"
                      className="px-6 py-2.5 text-gray-300 hover:text-white transition-colors text-sm text-center"
                    >
                      Cookie Settings
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
